import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type Account, type Category, type Claim, api, formatMoney } from "./api";
import { singaporeToday } from "./dates";
import { SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import {
  Actions,
  Alert,
  Badge,
  Box,
  Empty,
  IconAction,
  Loading,
  Modal,
  ModalActions,
  Table,
  Th,
  TwoLine,
  Working,
} from "./ui";

/**
 * Claims (quotation A12).
 *
 * Somebody pays for parking, a table fee or a box of sleeves out of their own pocket and
 * claims it back, with a photograph of the receipt. A master says yes or no; a yes becomes
 * a voucher to that person, so it is paid and matched against the bank like every other
 * payment. A refusal keeps the claim and the reason, because the person will ask.
 */
export function Claims({ fullName }: { readonly fullName: string }): React.ReactElement {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [canApprove, setCanApprove] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [deciding, setDeciding] = useState<{ claim: Claim; how: "approve" | "refuse" } | undefined>();
  const [looking, setLooking] = useState<Claim | undefined>();

  async function reload(): Promise<void> {
    try {
      const [mine, money, kinds] = await Promise.all([api.claims(), api.accounts(), api.categories()]);
      setClaims(mine.claims);
      setCanApprove(mine.canApprove);
      setAccounts(money.accounts);
      setCategories(kinds.categories);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load expense claims");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  if (loading) return <Loading />;

  const waiting = claims.filter((claim) => claim.status === "open");
  const owed = claims
    .filter((claim) => claim.status === "approved")
    .reduce((sum, claim) => sum + claim.amountCents, 0);

  return (
    <>
      <PageHeader
        title="Expense claims"
        subtitle={
          canApprove
            ? "Business expenses paid personally by staff, and their approval status."
            : "Business expenses you paid personally, and their approval status."
        }
        actions={
          <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setAdding(true)}>
            <i className="ri-add-line" /> Submit expense claim
          </button>
        }
      />

      {error ? (
        <Alert tone="danger" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      {waiting.length > 0 || owed > 0 ? (
        <Box title={canApprove ? "Claims summary" : "Your claims summary"}>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Awaiting approval</p>
              <p className="font-semibold text-[1.25rem] mb-0">
                {formatMoney(waiting.reduce((sum, claim) => sum + claim.amountCents, 0))}
                <span className="text-[0.813rem] font-normal text-[#8c9097] dark:text-white/50">
                  {" "}
                  · {waiting.length} {waiting.length === 1 ? "claim" : "claims"}
                </span>
              </p>
            </div>
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Approved, awaiting payment</p>
              <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(owed)}</p>
            </div>
          </div>
        </Box>
      ) : null}

      <Box title={canApprove ? "All expense claims" : "Your expense claims"} count={claims.length}>
        {claims.length === 0 ? (
          <Empty icon="ri-hand-coin-line">
            No expense claims have been submitted. If you paid a business expense personally, submit a claim here with a photo of the receipt.
          </Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Date</Th>
                <Th>Claim</Th>
                <Th end>Amount</Th>
                {canApprove ? <Th>Submitted by</Th> : null}
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {claims.map((claim) => (
              <tr key={claim.id} className="border-b border-defaultborder">
                <td>{claim.spentOn}</td>
                <td>
                  <TwoLine
                    main={
                      <span className="flex items-center gap-2">
                        {claim.description}
                        {claim.photoIds.length > 0 ? (
                          <button
                            type="button"
                            aria-label="Look at the receipt"
                            className="ti-btn ti-btn-light !py-0 !px-1 !text-[0.688rem] !mb-0"
                            onClick={() => setLooking(claim)}
                          >
                            <i className="ri-image-line" /> {claim.photoIds.length}
                          </button>
                        ) : null}
                      </span>
                    }
                    sub={[claim.number, claim.categoryName].filter(Boolean).join(" · ")}
                  />
                </td>
                <td className="!text-end font-semibold">{formatMoney(claim.amountCents)}</td>
                {canApprove ? <td className="text-[#8c9097] dark:text-white/50">{claim.claimedByName}</td> : null}
                <td>
                  <TwoLine
                    main={<Badge tone={TONES[claim.status]}>{LABELS[claim.status]}</Badge>}
                    sub={
                      claim.status === "refused"
                        ? (claim.decisionReason ?? undefined)
                        : claim.voucherNumber
                          ? claim.voucherNumber
                          : undefined
                    }
                  />
                </td>
                <td>
                  <Actions>
                    {canApprove && claim.status === "open" ? (
                      <>
                        <IconAction
                          icon="ri-check-line"
                          label="Agree it, and raise the voucher"
                          tip="Approve claim"
                          tone="success"
                          onClick={() => setDeciding({ claim, how: "approve" })}
                        />
                        <IconAction
                          icon="ri-close-line"
                          label="Refuse it, with the reason"
                          tip="Reject claim"
                          tone="danger"
                          onClick={() => setDeciding({ claim, how: "refuse" })}
                        />
                      </>
                    ) : null}
                    {!canApprove && claim.status === "open" ? (
                      <IconAction
                        icon="ri-arrow-go-back-line"
                        label="Take this claim back"
                        tone="secondary"
                        onClick={() =>
                          void api
                            .withdrawClaim(claim.id)
                            .then(() => {
                              setNotice(`${claim.number} withdrawn.`);
                              return reload();
                            })
                            .catch((problem: unknown) =>
                              setError(problem instanceof ApiError ? problem.message : "Could not withdraw the claim"),
                            )
                        }
                      />
                    ) : null}
                  </Actions>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Box>

      {adding ? (
        <NewClaim
          fullName={fullName}
          categories={categories}
          onClose={() => setAdding(false)}
          onDone={(message) => {
            setAdding(false);
            setNotice(message);
            void reload();
          }}
        />
      ) : null}

      {deciding ? (
        <Decide
          claim={deciding.claim}
          how={deciding.how}
          accounts={accounts}
          onClose={() => setDeciding(undefined)}
          onDone={(message) => {
            setDeciding(undefined);
            setNotice(message);
            void reload();
          }}
        />
      ) : null}

      {looking ? (
        <Modal open size="lg" title={`${looking.number}, ${looking.description}`} onClose={() => setLooking(undefined)}>
          <div className="grid grid-cols-12 gap-4">
            {looking.photoIds.map((id) => (
              <div key={id} className="md:col-span-6 col-span-12">
                <a href={`/api/photos/${id}`} target="_blank" rel="noreferrer">
                  <img
                    src={`/api/photos/${id}`}
                    alt="The receipt"
                    className="w-full rounded-sm border border-defaultborder dark:border-defaultborder/10"
                  />
                </a>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

const LABELS: Record<Claim["status"], string> = {
  open: "Awaiting approval",
  approved: "Approved",
  refused: "Rejected",
  paid: "Paid",
  withdrawn: "Withdrawn",
};

const TONES: Record<Claim["status"], "warning" | "info" | "danger" | "success" | "light"> = {
  open: "warning",
  approved: "info",
  refused: "danger",
  paid: "success",
  withdrawn: "light",
};

/** What was spent, on what, and the receipt. */
function NewClaim({
  fullName,
  categories,
  onClose,
  onDone,
}: {
  readonly fullName: string;
  readonly categories: readonly Category[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [spentOn, setSpentOn] = useState(singaporeToday());
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState<{ name: string; data: string; type: string } | undefined>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      const { claim } = await api.addClaim({
        spentOn,
        amount,
        description: description.trim(),
        categoryId: categoryId || null,
        note: note.trim() || null,
      });
      if (receipt) await api.addReceipt(claim.id, receipt.type, receipt.data);
      onDone(`${claim.number} submitted for approval.`);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The claim could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Submit expense claim"
      onClose={onClose}
      footer={
        <ModalActions
          busy={busy}
          disabled={amount.trim() === "" || description.trim() === ""}
          label="Submit claim"
          onCancel={onClose}
          onSave={() => void submit(new Event("submit") as unknown as FormEvent)}
        />
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="text-[#8c9097] dark:text-white/50">
        {fullName}, this claim goes to a system administrator for approval. Once approved, a payment voucher is
        issued to you and paid like any other payment.
      </p>

      <div className="grid grid-cols-12 gap-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="claim-date" className="form-label">
            Expense date
          </label>
          <input
            id="claim-date"
            type="date"
            className="form-control"
            value={spentOn}
            max={singaporeToday()}
            onChange={(changed) => setSpentOn(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="claim-amount" className="form-label">
            Amount (S$)
          </label>
          <input
            id="claim-amount"
            type="text"
            inputMode="decimal"
            className="form-control"
            placeholder="0.00"
            value={amount}
            onChange={(changed) => setAmount(changed.target.value)}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="claim-what" className="form-label">
            Expense description
          </label>
          <input
            id="claim-what"
            type="text"
            className="form-control"
            placeholder="Parking at Suntec, two days"
            value={description}
            onChange={(changed) => setDescription(changed.target.value)}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="claim-category" className="form-label">
            Transaction category <span className="text-[#8c9097] dark:text-white/50">(optional)</span>
          </label>
          <SelectField
            id="claim-category"
            value={categoryId}
            placeholder="Select category"
            options={categories
              .filter((category) => category.direction === "money_out")
              .map((category) => ({ value: category.id, label: category.name }))}
            onChange={setCategoryId}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="claim-receipt" className="form-label">
            Receipt
          </label>
          <input
            id="claim-receipt"
            type="file"
            accept="image/*"
            capture="environment"
            className="form-control"
            onChange={(changed) => {
              const file = changed.target.files?.[0];
              if (!file) return setReceipt(undefined);
              void file.arrayBuffer().then((buffer) => {
                const bytes = new Uint8Array(buffer);
                let binary = "";
                for (let at = 0; at < bytes.length; at += 0x8000) {
                  binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
                }
                setReceipt({ name: file.name, data: btoa(binary), type: file.type || "image/jpeg" });
              });
            }}
          />
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            {receipt ? `${receipt.name} ready to upload.` : "A photo of the receipt is sufficient. It can also be added later."}
          </p>
        </div>
        <div className="col-span-12">
          <label htmlFor="claim-note" className="form-label">
            Additional notes
          </label>
          <input
            id="claim-note"
            type="text"
            className="form-control"
            value={note}
            onChange={(changed) => setNote(changed.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/** Yes, from an account; or no, with the reason. */
function Decide({
  claim,
  how,
  accounts,
  onClose,
  onDone,
}: {
  readonly claim: Claim;
  readonly how: "approve" | "refuse";
  readonly accounts: readonly Account[];
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const company = accounts.filter((account) => account.ownerKind === "company" && account.isActive);
  const [accountId, setAccountId] = useState(company[0]?.id ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  async function go(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      if (how === "approve") {
        const { claim: after } = await api.approveClaim(claim.id, accountId, reason.trim() || null);
        onDone(`${after.number} approved. Payment voucher ${after.voucherNumber} issued to ${after.claimedByName}.`);
      } else {
        await api.refuseClaim(claim.id, reason.trim());
        onDone(`${claim.number} rejected.`);
      }
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The decision could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={how === "approve" ? `Approve claim ${claim.number}` : `Reject claim ${claim.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`ti-btn ${how === "approve" ? "ti-btn-success-full" : "ti-btn-danger-full"}`}
            disabled={busy || (how === "approve" ? accountId === "" : reason.trim() === "")}
            onClick={() => void go()}
          >
            {busy ? <Working label="Saving" /> : how === "approve" ? "Approve claim" : "Reject claim"}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="mb-1">
        <span className="font-semibold">{formatMoney(claim.amountCents)}</span> · {claim.description}
      </p>
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        {claim.claimedByName}, spent {claim.spentOn}
        {claim.photoIds.length > 0 ? ", receipt attached" : ", no receipt"}
      </p>

      {claim.photoIds.length > 0 ? (
        <div className="flex gap-2 my-3 overflow-x-auto">
          {claim.photoIds.map((id) => (
            <a key={id} href={`/api/photos/${id}`} target="_blank" rel="noreferrer" className="shrink-0">
              <img
                src={`/api/photos/${id}`}
                alt="The receipt"
                className="h-28 rounded-sm border border-defaultborder dark:border-defaultborder/10"
              />
            </a>
          ))}
        </div>
      ) : null}

      {how === "approve" ? (
        <>
          <label htmlFor="decide-account" className="form-label">
            Pay from account
          </label>
          <SelectField
            id="decide-account"
            value={accountId}
            options={company.map((account) => ({ value: account.id, label: account.name }))}
            onChange={setAccountId}
          />
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
            Approving creates a payment voucher to {claim.claimedByName}. Record its payment and match it to the bank transaction like any other voucher.
          </p>
        </>
      ) : null}

      <label htmlFor="decide-reason" className="form-label mt-3">
        {how === "approve" ? "Note (optional)" : "Reason for rejection"}
      </label>
      <input
        id="decide-reason"
        type="text"
        className="form-control"
        value={reason}
        onChange={(changed) => setReason(changed.target.value)}
      />
    </Modal>
  );
}
